/**
 * Billing API — powered by the stripe-billing Medplum Bot.
 *
 * Maintains the same public interface so existing components (AddCardDialog,
 * ClientPortalBillingPage, etc.) work without changes.
 *
 * Internally delegates to `medplum.executeBot()` via stripeBillingService.
 */

import type { MedplumClient } from '@medplum/core';
import * as svc from '../lib/stripeBillingService';
import { createStripeConfigCache } from '../lib/stripeBillingConfig';

export type { SavedPaymentMethod } from '../lib/stripeBillingService';
export type { PayInvoiceResult } from '../lib/stripeBillingService';
export type { StripeBillingConfig } from '../lib/stripeBillingService';

let medplumRef: MedplumClient | null = null;
const stripeConfigCache = createStripeConfigCache(() => svc.getStripeConfig(getMedplum()));

export function initializeBillingClient(medplum: MedplumClient): void {
  medplumRef = medplum;
  stripeConfigCache.clear();
}

function getMedplum(): MedplumClient {
  if (!medplumRef) {
    throw new Error('Billing API not initialized. Call initializeBillingClient first.');
  }
  return medplumRef;
}

function resolveUserId(patientId?: string): string {
  if (patientId) return patientId;
  const profile = getMedplum().getProfile();
  if (!profile?.id) {
    throw new Error('No authenticated user profile');
  }
  return profile.id;
}

export const billingApi = {
  async getStripeConfig(): Promise<svc.StripeBillingConfig> {
    return stripeConfigCache.get();
  },

  async createSetupIntent(
    patientId?: string,
  ): Promise<{ clientSecret: string; publishableKey: string }> {
    const userId = resolveUserId(patientId);
    return svc.createSetupIntent(getMedplum(), userId);
  },

  async syncPaymentMethod(params: {
    setupIntentId: string;
    nickname?: string;
    makeDefault?: boolean;
    patientId?: string;
  }): Promise<svc.SavedPaymentMethod> {
    const { patientId, ...rest } = params;
    const userId = resolveUserId(patientId);
    return svc.syncPaymentMethod(getMedplum(), { userId, ...rest });
  },

  async listPaymentMethods(
    _reconcile = false,
    patientId?: string,
  ): Promise<svc.SavedPaymentMethod[]> {
    const userId = resolveUserId(patientId);
    return svc.listPaymentMethods(getMedplum(), userId);
  },

  async detachPaymentMethod(
    stripePaymentMethodId: string,
    patientId?: string,
  ): Promise<void> {
    const userId = resolveUserId(patientId);
    return svc.detachPaymentMethod(getMedplum(), userId, stripePaymentMethodId);
  },

  async setDefaultPaymentMethod(
    stripePaymentMethodId: string,
    patientId?: string,
  ): Promise<void> {
    const userId = resolveUserId(patientId);
    return svc.setDefaultPaymentMethod(getMedplum(), userId, stripePaymentMethodId);
  },

  async payInvoice(params: {
    amountCents: number;
    currency?: string;
    description?: string;
    paymentMethodId?: string;
    patientId?: string;
  }): Promise<svc.PayInvoiceResult> {
    const { patientId, ...rest } = params;
    const userId = resolveUserId(patientId);
    return svc.payInvoice(getMedplum(), { userId, ...rest });
  },
};
